/**
 * Farm "Terminal" settings tab — runs shell commands on the farm's HOST machine
 * via POST /api/farms/:id/exec. The layout is split in two: a read-only dark
 * OUTPUT box (scrollback of past commands + their output) and, BELOW it, a
 * separate standalone command input row (a `$` sign + a light input field + a
 * Run button) that is visually distinct from the terminal output. Submitting a
 * command echoes a `$ <command>` line into the output box and appends the
 * result. Admin-only (gated by can('farm.exec') at the call site in
 * farm-settings-view.ts). Nothing runs automatically — only on Enter / Run.
 * The server is the security authority; this tab is just the UI.
 */
import { t } from '../ui/lang';
export function renderFarmTerminalTab(panel, farmId, _farmName) {
    panel.innerHTML = `
        <p class="settings-hint">${t('farmdev.term_hint')}</p>
        <div class="farm-term">
            <button class="farm-term__clear" id="farm-term-clear" type="button" title="${t('farmdev.clear')}" aria-label="${t('farmdev.clear')}">
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
            </button>
            <div class="farm-term__scrollback" id="farm-term-scrollback"></div>
        </div>
        <div class="farm-term-cmd">
            <span class="farm-term-cmd__sign" id="farm-term-sign" aria-hidden="true">$</span>
            <input class="farm-term-cmd__input" id="farm-term-input" type="text"
                placeholder="${t('farmdev.term_input_ph')}"
                autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" />
            <button class="farm-term-cmd__run modal__submit" id="farm-term-run" type="button">${t('farmdev.run')}</button>
        </div>
    `;
    const scrollback = panel.querySelector('#farm-term-scrollback');
    const input = panel.querySelector('#farm-term-input');
    const sign = panel.querySelector('#farm-term-sign');
    const runBtn = panel.querySelector('#farm-term-run');
    const clearBtn = panel.querySelector('#farm-term-clear');
    let running = false;
    // In-session command history (oldest → newest). historyIndex points at the
    // currently recalled entry; === history.length means "editing a fresh line".
    const history = [];
    let historyIndex = 0;
    // Holds the half-typed line when the user starts browsing history with Up.
    let draft = '';
    // The output box scrolls independently of the input row below it.
    const scrollToEnd = () => { scrollback.scrollTop = scrollback.scrollHeight; };
    const appendBlock = (cls, label, text) => {
        const block = document.createElement('div');
        block.className = `farm-term__block farm-term__block--${cls}`;
        if (label) {
            const lbl = document.createElement('span');
            lbl.className = 'farm-term__label';
            lbl.textContent = label;
            block.appendChild(lbl);
        }
        block.appendChild(document.createTextNode(text));
        scrollback.appendChild(block);
        scrollToEnd();
    };
    const clearScreen = () => {
        scrollback.innerHTML = '';
        scrollToEnd();
    };
    const setRunning = (busy) => {
        running = busy;
        input.disabled = busy;
        runBtn.disabled = busy;
        sign.classList.toggle('farm-term-cmd__sign--busy', busy);
    };
    const run = async (command) => {
        setRunning(true);
        appendBlock('cmd', '$ ', command);
        const pending = document.createElement('div');
        pending.className = 'farm-term__block farm-term__block--pending';
        pending.textContent = t('farmdev.term_running');
        scrollback.appendChild(pending);
        scrollToEnd();
        try {
            const r = await fetch(`/api/farms/${farmId}/exec`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ command }),
            });
            pending.remove();
            if (!r.ok) {
                let msg = `HTTP ${r.status}`;
                try {
                    const body = await r.json();
                    if (body.error)
                        msg = body.error;
                }
                catch { /* non-JSON error body */ }
                appendBlock('error', t('farmdev.term_error_label'), msg);
            }
            else {
                const result = await r.json();
                if (result.stdout)
                    appendBlock('stdout', '', result.stdout);
                if (result.stderr)
                    appendBlock('stderr', '', result.stderr);
                const codeStr = result.code === null ? '—' : String(result.code);
                const meta = result.timedOut
                    ? t('farmdev.exit_timed_out', { code: codeStr })
                    : t('farmdev.exit_code', { code: codeStr });
                appendBlock(result.timedOut || (result.code !== null && result.code !== 0) ? 'meta-bad' : 'meta', '', meta);
            }
        }
        catch (err) {
            pending.remove();
            appendBlock('error', t('farmdev.term_error_label'), err.message || t('farmdev.request_failed'));
        }
        finally {
            setRunning(false);
            input.focus();
            scrollToEnd();
        }
    };
    const submit = () => {
        if (running)
            return;
        const command = input.value.trim();
        if (!command)
            return;
        // Record in history (skip immediate duplicate of the last command).
        if (history[history.length - 1] !== command)
            history.push(command);
        historyIndex = history.length;
        draft = '';
        input.value = '';
        // Client-side special command: clear the scrollback without a server hit.
        if (command === 'clear' || command === 'cls') {
            clearScreen();
            input.focus();
            return;
        }
        void run(command);
    };
    const recall = (dir) => {
        if (history.length === 0)
            return;
        if (historyIndex === history.length) {
            // Leaving the fresh line — stash whatever is half-typed.
            if (dir === 1)
                return; // Down on a fresh line: nothing newer.
            draft = input.value;
        }
        const next = historyIndex + dir;
        if (next < 0)
            return; // Already at the oldest entry.
        if (next >= history.length) {
            // Stepped past the newest entry → back to the fresh draft line.
            historyIndex = history.length;
            input.value = draft;
        }
        else {
            historyIndex = next;
            input.value = history[next];
        }
        // Put the caret at end of the recalled command.
        const end = input.value.length;
        input.setSelectionRange(end, end);
    };
    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            submit();
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            recall(-1);
        }
        else if (e.key === 'ArrowDown') {
            e.preventDefault();
            recall(1);
        }
    });
    runBtn.addEventListener('click', () => { submit(); });
    clearBtn.addEventListener('click', () => { clearScreen(); input.focus(); });
    input.focus();
}
